import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { Button } from '../../../../components/general';

import useSampleProjectData from '../../../../hooks/useSampleProjectData';

import { Person } from '../../../../types';
import { getSampleSelected } from '../../../../utils';

import { UploadingPeople } from '../../components/types';
import PeopleSummary from './peopleSummary';

interface Props {
  setUploadedPeople: React.Dispatch<React.SetStateAction<Person[]>>;
  setUploadingPeople: React.Dispatch<React.SetStateAction<UploadingPeople>>;
}

const SampleComponent: React.FC<Props> = (props) => {
  const { eventId = '' } = useParams();

  const { setUploadedPeople, setUploadingPeople } = props;

  const { isSample, sampleProjectData, updateSampleProjectData } =
    useSampleProjectData(eventId);
  const samplePeople = useMemo(
    () => sampleProjectData.people ?? [],
    [sampleProjectData.people],
  );

  const togglePersonSelection = (id: string, isAdd?: boolean) => {
    const people = [...samplePeople];
    const foundPerson = people.find((person) => person.face.elementId === id);

    foundPerson.isSelected = isAdd ?? !foundPerson.isSelected;

    updateSampleProjectData({ ...sampleProjectData, people });
  };

  const handleSelectAll = () => {
    samplePeople.forEach((person: any) =>
      togglePersonSelection(person.face.elementId, true),
    );
  };

  const imitateUploading = () => {
    setUploadedPeople([]);

    // add all people to uploading list
    const uploadingPeopleTemp: UploadingPeople = {};
    samplePeople.forEach((person: any) => {
      const face = person.face;
      uploadingPeopleTemp[face.elementId] = {
        face: {
          id: face.elementId,
          name: face.name,
          progress: 0,
          created: new Date(),
        },
      };
    });
    setUploadingPeople(uploadingPeopleTemp);

    // imitate uploading a person every second
    const interval = setInterval(() => {
      setUploadedPeople((prevUploadedPeople) => {
        const nextPeople = [...prevUploadedPeople];

        if (nextPeople.length < samplePeople.length) {
          const uploadedPerson = samplePeople[prevUploadedPeople.length];
          nextPeople.push(uploadedPerson);

          // remove the person from uploading list
          setUploadingPeople((prevUploadingPeople) => {
            const uploadingPeopleTemp = { ...prevUploadingPeople };
            delete uploadingPeopleTemp[uploadedPerson.face.elementId];
            return uploadingPeopleTemp;
          });

          // last person was just 'uploaded'
          if (nextPeople.length === samplePeople.length) {
            clearInterval(interval);

            sampleProjectData.isPeopleUploaded = true;

            updateSampleProjectData(sampleProjectData);
          }
        }
        return nextPeople;
      });
    }, 1000);

    return () => clearInterval(interval);
  };

  const isBtnDisabled = useMemo(() => {
    return getSampleSelected(samplePeople).length < samplePeople.length;
  }, [samplePeople]);

  if (!isSample) return null;

  return (
    <React.Fragment>
      <Button onClick={handleSelectAll} variant="link" className="self-center">
        Select All
      </Button>

      <PeopleSummary
        people={samplePeople}
        isSample
        toggleSamplePerson={togglePersonSelection}
      />

      <Button
        onClick={imitateUploading}
        className="w-full sm:w-1/2 self-center"
        disabled={isBtnDisabled}
        allowPointerEvents
        popover={{
          header: 'Cannot upload',
          body: 'Select all people to upload',
          hide: !isBtnDisabled,
        }}
      >
        Upload
      </Button>
    </React.Fragment>
  );
};

export default SampleComponent;
